import React, { useMemo, useRef, useCallback, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { supabase } from '@/lib/supabase';
import './RichEditor.css';
import { 
  Table, Megaphone, Bold, Italic, Underline, Strikethrough, Quote, 
  List, ListOrdered, Indent, Outdent, Link as LinkIcon, Image as ImageIcon,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Type, Palette, Eraser
} from 'lucide-react';

// --- DEFINICIÓN DEL BLOQUE PERSONALIZADO (BLOT) ---
const BlockEmbed = Quill.import('blots/block/embed');

class CTABlot extends BlockEmbed {
  static create(value: any) {
    const node = super.create();
    node.setAttribute('class', 'blog-cta-placeholder');
    node.setAttribute('contenteditable', 'false');
    
    node.innerHTML = `
      <div class="cta-visual-content">
        <div class="cta-icon">📢</div>
        <div class="cta-text">
            <strong>BLOQUE DE PUBLICIDAD</strong>
            <span>(Buscador + Botón Publicar)</span>
        </div>
        <div class="cta-drag-handle">:: Arrastrar para mover</div>
      </div>
    `;
    return node;
  }

  static value(node: any) {
    return true;
  }
}

CTABlot.blotName = 'cta-block';
CTABlot.tagName = 'div';
CTABlot.className = 'blog-cta-placeholder';

if (!Quill.imports['formats/cta-block']) {
    Quill.register(CTABlot);
}

// --- COMPONENTE TOOLBAR PERSONALIZADA ---

interface CustomToolbarProps {
  id: string;
  handlers: {
    format: (fmt: string, val?: any) => void;
    insertTable: () => void;
    insertCTA: () => void;
    insertImage: () => void;
  };
}

const CustomToolbar = ({ id, handlers }: CustomToolbarProps) => {
  const preventFocusLoss = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div 
      id={id} 
      // CAMBIO: top-[85px] para respetar el header sticky de AdminBlogEditor
      // CAMBIO: rounded-t-lg para mantener estética sin overflow-hidden en el padre
      className="flex flex-wrap gap-1 items-center p-2 bg-slate-50 border-b border-slate-200 sticky top-[85px] z-40 rounded-t-lg shadow-sm"
      onMouseDown={preventFocusLoss} 
    >
      {/* Grupo: Texto */}
      <div className="flex items-center gap-0.5 mr-2 bg-white rounded-md border border-slate-200 p-0.5">
        <select 
            className="ql-header h-8 px-2 text-sm border-none outline-none bg-transparent cursor-pointer hover:bg-slate-50 rounded" 
            defaultValue=""
            onChange={e => handlers.format('header', e.target.value)}
        >
          <option value="2">Título 2</option>
          <option value="3">Título 3</option>
          <option value="">Normal</option>
        </select>
      </div>

      {/* Grupo: Formato Básico */}
      <div className="flex items-center gap-0.5 mr-2 bg-white rounded-md border border-slate-200 p-0.5">
        <ToolbarBtn icon={Bold} onClick={() => handlers.format('bold')} tooltip="Negrita" />
        <ToolbarBtn icon={Italic} onClick={() => handlers.format('italic')} tooltip="Cursiva" />
        <ToolbarBtn icon={Underline} onClick={() => handlers.format('underline')} tooltip="Subrayado" />
        <ToolbarBtn icon={Strikethrough} onClick={() => handlers.format('strike')} tooltip="Tachado" />
        <ToolbarBtn icon={Quote} onClick={() => handlers.format('blockquote')} tooltip="Cita" />
      </div>

      {/* Grupo: Listas e Indentación */}
      <div className="flex items-center gap-0.5 mr-2 bg-white rounded-md border border-slate-200 p-0.5">
        <ToolbarBtn icon={ListOrdered} onClick={() => handlers.format('list', 'ordered')} tooltip="Lista Numerada" />
        <ToolbarBtn icon={List} onClick={() => handlers.format('list', 'bullet')} tooltip="Lista de Puntos" />
        <div className="w-px h-4 bg-slate-200 mx-1"></div>
        <ToolbarBtn icon={Outdent} onClick={() => handlers.format('indent', '-1')} tooltip="Disminuir Sangría" />
        <ToolbarBtn icon={Indent} onClick={() => handlers.format('indent', '+1')} tooltip="Aumentar Sangría" />
      </div>

      {/* Grupo: Multimedia y Especiales */}
      <div className="flex items-center gap-0.5 bg-white rounded-md border border-slate-200 p-0.5">
        <ToolbarBtn icon={LinkIcon} onClick={() => {
            const url = prompt('Introduce la URL:');
            if (url) handlers.format('link', url);
        }} tooltip="Enlace" />
        
        <ToolbarBtn icon={ImageIcon} onClick={handlers.insertImage} tooltip="Imagen" />
        
        <button 
            type="button" 
            className="flex items-center justify-center w-auto px-2 h-8 rounded hover:bg-slate-100 text-slate-600 transition-colors"
            onClick={handlers.insertTable} 
            title="Insertar Tabla"
        >
            <Table className="w-4 h-4" />
            <span className="hidden sm:inline ml-1 text-xs font-medium">Tabla</span>
        </button>
        
        <button 
            type="button" 
            className="flex items-center justify-center w-auto px-2 h-8 rounded bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-100 transition-colors ml-1"
            onClick={handlers.insertCTA} 
            title="Insertar Bloque de Publicidad"
        >
            <Megaphone className="w-4 h-4" />
            <span className="hidden sm:inline ml-1 text-xs font-bold">ADS</span>
        </button>
      </div>

      {/* Limpiar */}
      <div className="ml-auto">
        <ToolbarBtn icon={Eraser} onClick={() => handlers.format('clean')} tooltip="Limpiar Formato" className="text-red-500 hover:bg-red-50" />
      </div>
    </div>
  );
};

// Componente auxiliar para botones de la toolbar
const ToolbarBtn = ({ icon: Icon, onClick, tooltip, className = '' }: any) => (
    <button
        type="button"
        onClick={onClick}
        title={tooltip}
        className={`flex items-center justify-center w-8 h-8 rounded hover:bg-slate-100 text-slate-600 transition-colors ${className}`}
        onMouseDown={(e) => e.preventDefault()} 
    >
        <Icon className="w-4 h-4" />
    </button>
);

interface RichEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function RichEditor({ value, onChange, placeholder }: RichEditorProps) {
  const quillRef = useRef<ReactQuill>(null);
  const lastSelectionRef = useRef<{ index: number, length: number } | null>(null);
  
  const toolbarId = useMemo(() => `toolbar-${Math.random().toString(36).substring(2)}`, []);

  const handleSelectionChange = (range: any, source: any, editor: any) => {
    if (range) {
        lastSelectionRef.current = range;
    }
  };

  const getEditorSafe = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return null;

    if (!quill.getSelection()) {
        if (lastSelectionRef.current) {
            quill.setSelection(lastSelectionRef.current);
        } else {
            quill.setSelection(quill.getLength(), 0);
        }
    }
    
    quill.focus();
    return quill;
  };

  const handleFormat = useCallback((format: string, value?: any) => {
    const quill = getEditorSafe();
    if (!quill) return;

    const range = quill.getSelection();
    if (!range) return; 

    if (format === 'clean') {
        quill.removeFormat(range.index, range.length);
        return;
    }

    if (format === 'list') {
        const currentFormat = quill.getFormat(range);
        if (currentFormat.list === value) {
            quill.format('list', false); 
        } else {
            quill.format('list', value);
        }
    } else if (value !== undefined) {
        quill.format(format, value);
    } else {
        const currentFormat = quill.getFormat(range);
        quill.format(format, !currentFormat[format]);
    }
  }, []);

  const insertImage = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        alert('La imagen es demasiado grande (Máx 5MB)');
        return;
      }

      try {
        const fileExt = file.name.split('.').pop();
        const fileName = `content_${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('blog-images')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data } = supabase.storage.from('blog-images').getPublicUrl(filePath);
        
        const quill = getEditorSafe();
        if (quill) {
            const range = quill.getSelection();
            if (range) {
                quill.insertEmbed(range.index, 'image', data.publicUrl);
                quill.setSelection(range.index + 1, 0);
            }
        }
      } catch (error: any) {
        console.error('Error uploading image:', error);
        alert('Error al subir la imagen: ' + error.message);
      }
    };
  }, []);

  const insertTable = useCallback(() => {
    const quill = getEditorSafe();
    if (quill) {
        const range = quill.getSelection();
        if (range) {
            const tableHTML = `
              <table style="width:100%; border-collapse: collapse;">
                <thead>
                  <tr><th>Encabezado 1</th><th>Encabezado 2</th></tr>
                </thead>
                <tbody>
                  <tr><td>Dato 1</td><td>Dato 2</td></tr>
                </tbody>
              </table>
              <p><br/></p>
            `;
            quill.clipboard.dangerouslyPasteHTML(range.index, tableHTML);
        }
    }
  }, []);

  const insertCTA = useCallback(() => {
    const quill = getEditorSafe();
    if (quill) {
        const range = quill.getSelection();
        if (range) {
            quill.insertEmbed(range.index, 'cta-block', true);
            quill.setSelection(range.index + 1, 0);
        }
    }
  }, []);

  const modules = useMemo(() => ({
    toolbar: {
      container: `#${toolbarId}`,
    },
    clipboard: {
      matchVisual: false
    }
  }), [toolbarId]);

  const formats = [
    'header', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'indent',
    'link', 'image', 'video',
    'color', 'background', 'align',
    'cta-block'
  ];

  const handlers = {
    format: handleFormat,
    insertTable,
    insertCTA,
    insertImage
  };

  return (
    // CAMBIO CRÍTICO: Eliminado 'overflow-hidden' para permitir sticky
    <div className="rich-editor-container border border-slate-300 rounded-lg bg-white">
      <CustomToolbar id={toolbarId} handlers={handlers} />
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        onChangeSelection={handleSelectionChange} 
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        className="bg-white min-h-[400px]"
      />
    </div>
  );
}
